import { useEffect, useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import {
  AuthFactoryProvider,
  LoadingOverlay,
  useAuth,
} from '@spotify/confidence-core-react';
import { FeatureFlagged } from '@spotify/confidence-flag-evaluation-react';
import {
  TrackingScope,
  withPageImpression,
} from '@spotify/plugin-confidence-tracking-react';

import { AccountCreated } from './AccountCreated';
import { CreateAccountForm } from './CreateAccountForm';
import { CreateAccountFormV2 } from './CreateAccountFormV2';
import { InviteError } from './InviteError';
import { CreatedAccount, useInvite, useRegionCheck } from './hooks';
import { getSignupReturnPath } from './signupUtils';

const CLIENT_ID = '82qMvwZvqd3t3S0gRDvs8R53TehQXSJY';
const handleSignupTokenReceived = () => undefined;

const SignUpJourney = ({ inviteId }: { inviteId?: string }) => {
  const [createdAccount, setCreatedAccount] = useState<CreatedAccount>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: appAuthLoading } = useAuth();

  const [{ error: inviteError, loading, value: inviteData }, getAccountInvite] =
    useInvite(inviteId);

  const account = createdAccount ?? inviteData?.createdAccount;

  const {
    loading: regionLoading,
    error: regionError,
    regionData,
  } = useRegionCheck();

  useEffect(() => {
    const abortController = new AbortController();
    if (inviteId) {
      void getAccountInvite(abortController.signal);
    }
    return () => abortController.abort();
  }, [getAccountInvite, inviteId]);

  useEffect(() => {
    if (regionError) {
      // eslint-disable-next-line no-console
      console.warn(
        'Region check failed, allowing signup to proceed:',
        regionError,
      );
      return;
    }

    if (!regionLoading && regionData?.allowed === false) {
      navigate('/signup/unsupported-market');
    }
  }, [regionLoading, regionError, regionData, navigate]);

  if (!inviteId && appAuthLoading) {
    return <LoadingOverlay loading />;
  }

  if (!inviteId && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (loading || regionLoading) {
    return <LoadingOverlay loading />;
  }
  if (inviteError) {
    return <InviteError error={inviteError} />;
  }
  if (account) {
    return <AccountCreated createdAccount={account} />;
  }
  return (
    <AuthFactoryProvider
      clientId={CLIENT_ID}
      mode="signup"
      onTokenReceived={handleSignupTokenReceived}
    >
      <TrackingScope elementId="main.signup">
        <FeatureFlagged name="slim-signup-wizard">
          <FeatureFlagged.On>
            <CreateAccountFormV2
              invite={inviteData}
              inviteId={inviteId}
              onCreate={setCreatedAccount}
            />
          </FeatureFlagged.On>
          <FeatureFlagged.Off>
            <CreateAccountForm
              inviteId={inviteId}
              onCreate={setCreatedAccount}
              skipCompanyWizard={inviteData?.warehouseNative ?? false}
            />
          </FeatureFlagged.Off>
        </FeatureFlagged>
      </TrackingScope>
    </AuthFactoryProvider>
  );
};

const SignUpPageComponent = () => {
  const { id: inviteId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const signupReturnPath = getSignupReturnPath({
    returnTo: searchParams.get('signupReturnTo'),
    origin: window.location.origin,
  });

  if (
    signupReturnPath &&
    signupReturnPath !==
      `${location.pathname}${location.search}${location.hash}`
  ) {
    return <Navigate to={signupReturnPath} replace />;
  }

  return <SignUpJourney key={inviteId ?? 'regular'} inviteId={inviteId} />;
};

export const SignUpPage = withPageImpression(SignUpPageComponent, {
  pageId: 'signup',
});
